import { forwardRef, useCallback, useId, useState } from "react";

import { Eye, EyeOff, Lock } from "lucide-react";

import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/utils";

type NativeInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">;

interface PasswordFieldProps extends NativeInputProps {
  /** Clases para el wrapper (no para el `<input>`). */
  wrapperClassName?: string;
}

/**
 * Input de contraseña blindado:
 *  - Toggle de visibilidad con `aria-pressed`.
 *  - Detector de Caps-Lock usando `KeyboardEvent.getModifierState` — el
 *    único método confiable; `event.key === "CapsLock"` no funciona en
 *    todos los teclados ni al presionar otras teclas con caps activo.
 *  - `aria-describedby` cableado dinámicamente al mensaje de caps lock
 *    para que screen readers lo anuncien.
 *  - `forwardRef` para integración directa con React Hook Form.
 */
export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField(
    {
      className,
      wrapperClassName,
      onKeyDown,
      onKeyUp,
      onBlur,
      "aria-describedby": ariaDescribedBy,
      ...props
    },
    ref,
  ) {
    const [visible, setVisible] = useState(false);
    const [capsOn, setCapsOn] = useState(false);
    const capsId = useId();

    const detectCaps = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      // `getModifierState` es el único método confiable cross-browser.
      const on = e.getModifierState?.("CapsLock") ?? false;
      setCapsOn(on);
    }, []);

    const combinedDescribedBy = capsOn
      ? [ariaDescribedBy, capsId].filter(Boolean).join(" ")
      : ariaDescribedBy;

    return (
      <div className={cn("space-y-1.5", wrapperClassName)}>
        <div className="relative">
          <Lock
            aria-hidden
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            ref={ref}
            type={visible ? "text" : "password"}
            autoComplete="current-password"
            spellCheck={false}
            aria-describedby={combinedDescribedBy}
            onKeyDown={(e) => {
              detectCaps(e);
              onKeyDown?.(e);
            }}
            onKeyUp={(e) => {
              detectCaps(e);
              onKeyUp?.(e);
            }}
            onBlur={(e) => {
              setCapsOn(false);
              onBlur?.(e);
            }}
            className={cn(
              "h-12 pl-10 pr-11 text-[15px]",
              "transition-shadow focus-visible:shadow-ring-focus",
              className,
            )}
            {...props}
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
            aria-pressed={visible}
            tabIndex={-1}
            className={cn(
              "absolute right-1.5 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors",
              "hover:bg-accent/40 hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>

        {capsOn ? (
          <p
            id={capsId}
            role="status"
            className="flex animate-fade-in items-center gap-1.5 text-[12px] font-medium text-amber-700 dark:text-amber-200"
          >
            <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
            Mayúsculas activadas (Caps Lock)
          </p>
        ) : null}
      </div>
    );
  },
);
